// Employee Lifecycle Packages (L1+L2). Onboarding/offboarding checklists owned
// by employee-service: per-tenant package definitions (seeded defaults), and
// per-employee instances whose computed tasks read LIVE state (readiness,
// documents, payroll assignment). The gateway exposes everything under
// /api/v1/lifecycle/*; gatewayApi attaches the access token and handles refresh.
import { gatewayApi } from '@/lib/hrApi';

export type LifecycleType = 'onboarding' | 'offboarding';
export type LifecycleStatus = 'in_progress' | 'completed' | 'cancelled';
export type LifecycleStatusFilter = LifecycleStatus | 'all';
export type AssigneeRole = 'hr' | 'manager' | 'finance' | 'employee' | 'admin';
export type ExitType =
  | 'resignation' | 'termination' | 'end_of_contract'
  | 'redundancy' | 'retirement' | 'absconded';

/** One task in a package definition. */
export interface LifecycleTaskDef {
  key: string;
  title: string;
  assignee_role: AssigneeRole | string;
  required: boolean;
  /** 'manual' | 'readiness:profile' | 'readiness:statutory' | 'readiness:attendance'
   *  | 'readiness:contact' | 'documents' | 'payroll_assigned' */
  verify: string;
}

/** What happens when an instance completes. */
export interface LifecycleCompletion {
  set_status: string;
  set_payroll_enabled: boolean;
  require_all_required_tasks: boolean;
}

export interface LifecyclePackage {
  name: string;
  type: LifecycleType;
  /** Employment-type keys this package applies to; empty = any (fallback). */
  applies_to_employment_types: string[];
  /** Exit types this package applies to (offboarding only); empty = any exit
   * type (fallback). Ignored for onboarding packages. */
  applies_to_exit_types?: string[];
  tasks: LifecycleTaskDef[];
  completion: LifecycleCompletion;
  /** True when the tenant customized this package (vs a seeded default). */
  custom: boolean;
}

export interface LifecyclePackagesResponse {
  /** The tenant's effective packages (defaults until customized). */
  packages: LifecyclePackage[];
  /** The platform defaults, for "Reset to defaults". */
  defaults: LifecyclePackage[];
  /** Valid assignee roles. */
  roles: string[];
  /** Valid verify kinds. */
  verify_kinds: string[];
  /** Valid exit types for offboarding (matching + start dialog). */
  exit_types: string[];
}

/** Compact employee shape embedded in instance responses. */
export interface LifecycleEmployeeRef {
  id: string;
  name: string;
  employee_code?: string | null;
  status?: string | null;
  employment_type?: string | null;
}

/** Cheap list row (GET /lifecycle/instances). */
export interface LifecycleInstanceRow {
  id: string;
  type: LifecycleType;
  package_name: string;
  status: LifecycleStatus | string;
  started_at?: string | null;
  completed_at?: string | null;
  /** Exit metadata (offboarding only; null for onboarding). */
  exit_type: string | null;
  last_working_date: string | null;
  final_payroll_date: string | null;
  /** True when in_progress and the final payroll date has passed. */
  overdue: boolean;
  employee: LifecycleEmployeeRef;
}

/** Per-task state in the full instance view. Computed tasks (computed=true)
 * mirror live platform state and cannot be completed manually. */
export interface LifecycleTaskState {
  id: string;
  key: string;
  title: string;
  assignee_role: AssigneeRole | string;
  required: boolean;
  verify: string;
  done: boolean;
  computed: boolean;
  /** For computed tasks: what's still missing (e.g. "Missing: TIN, NSSF"). */
  detail?: string | null;
  note?: string | null;
  completed_by?: string | null;
  completed_at?: string | null;
}

export interface LifecycleProgress {
  required_total: number;
  required_done: number;
  total: number;
  done: number;
  ready_to_complete: boolean;
}

export interface LifecycleInstanceView {
  id: string;
  employee_id: string;
  type: LifecycleType;
  package_name: string;
  status: LifecycleStatus | string;
  completion: LifecycleCompletion;
  started_at?: string | null;
  completed_at?: string | null;
  /** Exit metadata (offboarding only; null for onboarding). */
  exit_type: string | null;
  last_working_date: string | null;
  final_payroll_date: string | null;
  reason: string | null;
  /** True when in_progress and the final payroll date has passed. */
  overdue: boolean;
  tasks: LifecycleTaskState[];
  progress: LifecycleProgress;
  employee: LifecycleEmployeeRef;
}

export interface StartInstanceResult {
  id: string;
  package_name: string;
  type: LifecycleType;
}

export interface CompleteInstanceResult {
  status: string;
  employee_status: string;
  payroll_enabled: boolean;
  /** e.g. why payroll couldn't be flipped — surface it in the success toast. */
  payroll_note?: string | null;
  /** Multi-entity Phase 6: only this employment ended — the person continues with another company. */
  engagement_note?: string | null;
}

const BASE = '/api/v1/lifecycle';

// ------------------------------------------------------------------ packages
/** GET the tenant's packages + defaults + valid roles/verify kinds (manager+). */
export const getLifecyclePackages = async (): Promise<LifecyclePackagesResponse> => {
  const { data } = await gatewayApi.get<LifecyclePackagesResponse>(`${BASE}/packages`);
  return data;
};

/** PUT the full package set (tenant admin). `[]` resets to the platform
 * defaults. 400 with a message on invalid packages. */
export const putLifecyclePackages = async (
  packages: Omit<LifecyclePackage, 'custom'>[],
): Promise<LifecyclePackagesResponse> => {
  const { data } = await gatewayApi.put<LifecyclePackagesResponse>(`${BASE}/packages`, { packages });
  return data;
};

// ----------------------------------------------------------------- instances
/** Start an onboarding/offboarding instance (employee:write). 409 when one is
 * already in progress for the employee. Omit package_name for auto-match
 * (offboarding auto-matches by exit_type). Offboarding REQUIRES exit_type +
 * last_working_date — the backend 409s with a message otherwise. */
export const startLifecycleInstance = async (input: {
  employee_id: string;
  type: LifecycleType;
  package_name?: string;
  exit_type?: string;
  /** YYYY-MM-DD */
  last_working_date?: string;
  /** YYYY-MM-DD */
  final_payroll_date?: string;
  /** ≤2000 chars */
  reason?: string;
}): Promise<StartInstanceResult> => {
  const { data } = await gatewayApi.post<StartInstanceResult>(`${BASE}/instances`, input);
  return data;
};

/** List instances (manager+; cheap list — no task payloads). */
export const listLifecycleInstances = async (
  status: LifecycleStatusFilter = 'in_progress',
  type?: LifecycleType,
): Promise<LifecycleInstanceRow[]> => {
  const { data } = await gatewayApi.get<{ instances: LifecycleInstanceRow[] }>(`${BASE}/instances`, {
    params: { status, ...(type ? { type } : {}) },
  });
  return data.instances || [];
};

/** Full task-board view of one instance (manager+). */
export const getLifecycleInstance = async (id: string): Promise<LifecycleInstanceView> => {
  const { data } = await gatewayApi.get<LifecycleInstanceView>(`${BASE}/instances/${id}`);
  return data;
};

/** The employee's current instance (or null when none is in progress). */
export const getEmployeeLifecycle = async (
  employeeId: string,
): Promise<LifecycleInstanceView | null> => {
  const { data } = await gatewayApi.get<{ instance: LifecycleInstanceView | null }>(
    `/api/v1/employees/${employeeId}/lifecycle`,
  );
  return data.instance ?? null;
};

// --------------------------------------------------------------------- tasks
/** Mark a manual task done (manager+). 409 for computed tasks. */
export const completeLifecycleTask = async (taskId: string, note?: string): Promise<void> => {
  await gatewayApi.post(`${BASE}/tasks/${taskId}/complete`, note?.trim() ? { note: note.trim() } : {});
};

/** Reopen a manual task (manager+). 409 for computed tasks. */
export const reopenLifecycleTask = async (taskId: string): Promise<void> => {
  await gatewayApi.post(`${BASE}/tasks/${taskId}/reopen`);
};

// ---------------------------------------------------------------- completion
/** Complete the instance (needs employee:change:approve). 409 lists the
 * pending required tasks. On success flips the employee status + payroll flag
 * per the package's completion rules. */
export const completeLifecycleInstance = async (id: string): Promise<CompleteInstanceResult> => {
  const { data } = await gatewayApi.post<CompleteInstanceResult>(`${BASE}/instances/${id}/complete`);
  return data;
};

/** Cancel an in-progress instance (employee:write). */
export const cancelLifecycleInstance = async (id: string): Promise<void> => {
  await gatewayApi.post(`${BASE}/instances/${id}/cancel`);
};

// ------------------------------------------------------------------- helpers
/** Lane render order on the task board. */
export const ASSIGNEE_ROLE_ORDER: AssigneeRole[] = ['hr', 'manager', 'finance', 'employee', 'admin'];

export const assigneeRoleLabel = (role: string): string => {
  switch (role) {
    case 'hr': return 'HR';
    case 'manager': return 'Manager';
    case 'finance': return 'Finance';
    case 'employee': return 'Employee';
    case 'admin': return 'Admin';
    default: return role ? role.charAt(0).toUpperCase() + role.slice(1) : role;
  }
};

/** Humanized labels for the verify kinds (package builder select). */
export const verifyKindLabel = (kind: string): string => {
  switch (kind) {
    case 'manual': return 'Confirmed manually by the assignee';
    case 'readiness:profile': return 'Checked automatically: profile basics complete';
    case 'readiness:statutory': return 'Checked automatically: statutory IDs complete';
    case 'readiness:attendance': return 'Checked automatically: attendance mapping set';
    case 'readiness:contact': return 'Checked automatically: contact details complete';
    case 'documents': return 'Checked automatically: required documents on file';
    case 'payroll_assigned': return 'Checked automatically: payroll profile assigned';
    case 'terminal_dues': return 'Checked automatically: final settlement computed in Payroll';
    case 'account_deactivated': return 'Checked automatically: KaziPeople account deactivated';
    default: return kind;
  }
};

/** Exit types in display order (fallback if the backend list is unavailable). */
export const FALLBACK_EXIT_TYPES: ExitType[] = [
  'resignation', 'termination', 'end_of_contract', 'redundancy', 'retirement', 'absconded',
];

/** Human label for an exit type, i18n-aware (pass the namespace's `t`). */
export const exitTypeLabel = (
  t: (key: string, options?: { defaultValue: string }) => string,
  exitType: string,
): string => {
  switch (exitType) {
    case 'resignation': return t('lifecycle.exit_resignation', { defaultValue: 'Resignation' });
    case 'termination': return t('lifecycle.exit_termination', { defaultValue: 'Termination' });
    case 'end_of_contract': return t('lifecycle.exit_end_of_contract', { defaultValue: 'End of contract' });
    case 'redundancy': return t('lifecycle.exit_redundancy', { defaultValue: 'Redundancy' });
    case 'retirement': return t('lifecycle.exit_retirement', { defaultValue: 'Retirement' });
    case 'absconded': return t('lifecycle.exit_absconded', { defaultValue: 'Absconded' });
    default: return exitType ? exitType.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : exitType;
  }
};

/** Fallback verify kinds if the backend list is unavailable. */
export const FALLBACK_VERIFY_KINDS = [
  'manual',
  'readiness:profile',
  'readiness:statutory',
  'readiness:attendance',
  'readiness:contact',
  'documents',
  'payroll_assigned',
  'terminal_dues',
  'account_deactivated',
];

/** Derive the completion rules from the package type (kept read-only in the
 * builder so packages can't invent their own status transitions). */
export const completionForType = (type: LifecycleType): LifecycleCompletion => (
  type === 'offboarding'
    ? { set_status: 'Terminated', set_payroll_enabled: false, require_all_required_tasks: true }
    : { set_status: 'Active', set_payroll_enabled: true, require_all_required_tasks: true }
);
